import {
  PanelBody,
  SelectControl,
  ToggleControl,
  FormTokenField,
  RangeControl,
  __experimentalNumberControl as NumberControl,
} from "@wordpress/components";
import { store as coreStore } from "@wordpress/core-data";
import { useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";

// Keep in sync with ORDER_BY in Helpers/ProductCollectionQuery.php, which
// whitelists these keys before they reach WP_Query.
const orderByOptions = [
  { label: __("Newest first", "brandy-blocks"), value: "date" },
  { label: __("Title", "brandy-blocks"), value: "title" },
  { label: __("Price", "brandy-blocks"), value: "price" },
  {
    label: __("Popularity (total sales)", "brandy-blocks"),
    value: "popularity",
  },
  { label: __("Average rating", "brandy-blocks"), value: "rating" },
  { label: __("Menu order", "brandy-blocks"), value: "menu_order" },
  { label: __("Random", "brandy-blocks"), value: "rand" },
];

const orderOptions = [
  { label: __("Descending", "brandy-blocks"), value: "desc" },
  { label: __("Ascending", "brandy-blocks"), value: "asc" },
];

/**
 * Term picker backed by FormTokenField. Attributes store term IDs (that is what
 * the PHP tax_query wants); the field speaks names, so the two are mapped here.
 */
function TermTokenField({ taxonomy, label, help, value = [], onChange }) {
  const terms = useSelect(
    (select) =>
      select(coreStore).getEntityRecords("taxonomy", taxonomy, {
        per_page: -1,
        _fields: "id,name",
        context: "view",
      }),
    [taxonomy],
  );

  const suggestions = (terms ?? []).map((term) => term.name);
  const selectedNames = (value ?? [])
    .map((id) => (terms ?? []).find((term) => term.id === id)?.name)
    .filter(Boolean);

  return (
    <FormTokenField
      __next40pxDefaultSize
      __nextHasNoMarginBottom
      label={label}
      help={help}
      value={selectedNames}
      suggestions={suggestions}
      onChange={(names) =>
        onChange(
          names
            .map((name) => (terms ?? []).find((term) => term.name === name)?.id)
            .filter((id) => typeof id === "number"),
        )
      }
    />
  );
}

export default function QueryPanel({ query, updateQuery }) {
  const inherit = !!query.inherit;

  return (
    <PanelBody title={__("Products", "brandy-blocks")}>
      <ToggleControl
        __nextHasNoMarginBottom
        label={__("Inherit query from template", "brandy-blocks")}
        help={__(
          "On shop and product category archives, show that archive's own results — including its sorting, filtering and paging. The controls below do not apply.",
          "brandy-blocks",
        )}
        checked={inherit}
        onChange={(value) => updateQuery({ inherit: value })}
      />
      <ToggleControl
        __nextHasNoMarginBottom
        label={__("Paginated", "brandy-blocks")}
        help={__(
          "Adds page-by-page navigation. Insert a Pagination block inside this collection to render the links.",
          "brandy-blocks",
        )}
        checked={!!query.pagination}
        onChange={(value) => updateQuery({ pagination: value })}
      />
      {inherit ? null : (
        <>
          <RangeControl
            __nextHasNoMarginBottom
            __next40pxDefaultSize
            label={__("Products per page", "brandy-blocks")}
            value={query.perPage ?? 8}
            min={1}
            max={48}
            onChange={(value) => updateQuery({ perPage: value })}
          />
          <NumberControl
            __next40pxDefaultSize
            label={__("Offset", "brandy-blocks")}
            help={__(
              "Skip this many products before the first one shown.",
              "brandy-blocks",
            )}
            value={query.offset ?? 0}
            min={0}
            onChange={(value) =>
              updateQuery({ offset: parseInt(value, 10) || 0 })
            }
          />
          <SelectControl
            __nextHasNoMarginBottom
            __next40pxDefaultSize
            label={__("Order by", "brandy-blocks")}
            value={query.orderBy ?? "date"}
            options={orderByOptions}
            onChange={(value) => updateQuery({ orderBy: value })}
          />
          {"rand" !== (query.orderBy ?? "date") && (
            <SelectControl
              __nextHasNoMarginBottom
              __next40pxDefaultSize
              label={__("Order", "brandy-blocks")}
              value={query.order ?? "desc"}
              options={orderOptions}
              onChange={(value) => updateQuery({ order: value })}
            />
          )}
          <TermTokenField
            taxonomy="product_cat"
            label={__("Product categories", "brandy-blocks")}
            help={__("Leave empty for all categories.", "brandy-blocks")}
            value={query.categories}
            onChange={(ids) => updateQuery({ categories: ids })}
          />
          <TermTokenField
            taxonomy="product_tag"
            label={__("Product tags", "brandy-blocks")}
            value={query.tags}
            onChange={(ids) => updateQuery({ tags: ids })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Featured products only", "brandy-blocks")}
            checked={!!query.featured}
            onChange={(value) => updateQuery({ featured: value })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("On sale only", "brandy-blocks")}
            checked={!!query.onSale}
            onChange={(value) => updateQuery({ onSale: value })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Hide out of stock", "brandy-blocks")}
            checked={!!query.hideOutOfStock}
            onChange={(value) => updateQuery({ hideOutOfStock: value })}
          />
          <ToggleControl
            __nextHasNoMarginBottom
            label={__("Related to the current product", "brandy-blocks")}
            help={__(
              "On a single product page, limit results to products sharing its categories or tags. Ignored elsewhere.",
              "brandy-blocks",
            )}
            checked={!!(query.relatedBy?.categories || query.relatedBy?.tags)}
            onChange={(value) =>
              updateQuery({
                relatedBy: { categories: value, tags: value },
                excludeCurrent: value,
              })
            }
          />
        </>
      )}
    </PanelBody>
  );
}
